/**
 * S-3022: Cloudflare Stream Playback URL Resolver
 * 
 * Resolves video playback URLs with priority:
 * 1. Cloudflare Stream (if cf_stream_uid present and feature enabled)
 * 2. Mux (if mux_playback_id present)
 * 3. Error if neither available
 * 
 * Feature flag: VITE_USE_CLOUDFLARE_STREAM
 */

export interface VideoSource {
  id: string;
  cf_stream_uid?: string | null;
  cf_playback_url?: string | null;
  mux_playback_id?: string | null;
  playback_policy?: string | null;
  status?: string;
  legacy_video_id?: string | null;
}

export interface PlaybackUrl {
  url: string;
  type: 'cloudflare' | 'mux';
  format: 'hls' | 'iframe';
  thumbnail?: string;
  isReady: boolean;
  requiresSigning?: boolean;
}

export type PlaybackFormat = 'hls' | 'iframe';

/**
 * Check if Cloudflare Stream is enabled (feature flag)
 */
export function isCloudflareStreamEnabled(): boolean {
  const featureFlag = import.meta.env.VITE_USE_CLOUDFLARE_STREAM;
  
  // Explicitly check for 'true' string (case-insensitive)
  if (typeof featureFlag === 'string') {
    return featureFlag.toLowerCase() === 'true';
  }
  
  // Default to false if not set or invalid
  return false;
}

/**
 * Resolve playback URL with Cloudflare priority
 * 
 * Priority order when feature flag enabled:
 * 1. Cloudflare Stream (cf_stream_uid)
 * 2. Mux (mux_playback_id) - fallback
 * 
 * Priority order when feature flag disabled:
 * 1. Mux (mux_playback_id)
 * 2. Cloudflare Stream (cf_stream_uid) - fallback
 */
export function resolvePlaybackUrl(
  video: VideoSource,
  preferIframe: boolean = false
): PlaybackUrl | null {
  const cloudflareEnabled = isCloudflareStreamEnabled();

  console.log('[S-3022] Resolving playback URL:', {
    video_id: video.id,
    has_cf_uid: !!video.cf_stream_uid,
    has_mux_id: !!video.mux_playback_id,
    status: video.status,
    cloudflare_enabled: cloudflareEnabled,
    prefer_iframe: preferIframe,
  });

  // Priority 1: Cloudflare Stream (if feature enabled and uid present)
  if (cloudflareEnabled && video.cf_stream_uid) {
    return resolveCloudflarePlayback(video, preferIframe);
  }

  // Priority 2: Mux (legacy or fallback)
  if (video.mux_playback_id) {
    return resolveMuxPlayback(video);
  }

  // Priority 3: Cloudflare as fallback (if feature disabled but CF uid exists)
  if (!cloudflareEnabled && video.cf_stream_uid) {
    console.warn('[S-3022] Cloudflare Stream available but feature flag disabled');
    return resolveCloudflarePlayback(video, preferIframe);
  }

  // No playback URL available
  console.error('[S-3022] No playback URL available:', {
    video_id: video.id,
    has_cf_uid: !!video.cf_stream_uid,
    has_mux_id: !!video.mux_playback_id,
  });

  return null;
}

/**
 * Resolve Cloudflare Stream playback URL
 */
function resolveCloudflarePlayback(
  video: VideoSource,
  preferIframe: boolean
): PlaybackUrl {
  const uid = video.cf_stream_uid!;

  // Use iframe embed if preferred or explicitly provided cf_playback_url is an iframe
  const useIframe = preferIframe || (video.cf_playback_url?.includes('iframe.cloudflarestream.com') ?? false);

  const url = useIframe
    ? `https://iframe.cloudflarestream.com/${uid}`
    : video.cf_playback_url || `https://watch.cloudflarestream.com/${uid}`;

  const thumbnail = `https://videodelivery.net/${uid}/thumbnails/thumbnail.jpg`;

  // Cloudflare Stream videos are optimistically ready
  // Status can be 'processing' (upload complete, encoding) or 'ready'
  const isReady = video.status === 'ready' || video.status === 'processing';

  console.log('[S-3022] Using Cloudflare Stream:', {
    uid,
    format: useIframe ? 'iframe' : 'hls',
    url,
    is_ready: isReady,
  });

  return {
    url,
    type: 'cloudflare',
    format: useIframe ? 'iframe' : 'hls',
    thumbnail,
    isReady,
    requiresSigning: false, // Cloudflare Stream URLs are not signed
  };
}

/**
 * Resolve Mux playback URL
 */
function resolveMuxPlayback(video: VideoSource): PlaybackUrl {
  const playbackId = video.mux_playback_id!;
  const isPublic = video.playback_policy === 'public';

  // Mux HLS URL (public or requires signing)
  const url = `https://stream.mux.com/${playbackId}.m3u8`;
  const thumbnail = `https://image.mux.com/${playbackId}/thumbnail.jpg`;

  const isReady = video.status === 'ready';

  console.log('[S-3022] Using Mux:', {
    playback_id: playbackId,
    is_public: isPublic,
    url,
    is_ready: isReady,
    requires_signing: !isPublic,
  });

  return {
    url,
    type: 'mux',
    format: 'hls',
    thumbnail,
    isReady,
    requiresSigning: !isPublic, // Private Mux videos require signed URLs
  };
}

/**
 * Get iframe embed URL for Cloudflare Stream
 */
export function getCloudflareIframeUrl(uid: string): string {
  return `https://iframe.cloudflarestream.com/${uid}`;
}

/**
 * Get HLS playback URL for Cloudflare Stream
 */
export function getCloudflareHlsUrl(uid: string): string {
  return `https://watch.cloudflarestream.com/${uid}`;
}

/**
 * Get thumbnail URL for Cloudflare Stream
 * 
 * Note: Cloudflare uses videodelivery.net for thumbnails
 */
export function getCloudflareThumbnailUrl(uid: string, options?: {
  time?: number; // Timestamp in seconds
  width?: number;
  height?: number;
  fit?: 'clip' | 'crop' | 'scale' | 'fill';
}): string {
  let url = `https://videodelivery.net/${uid}/thumbnails/thumbnail.jpg`;

  // Add optional query parameters
  const params = new URLSearchParams();

  if (options?.time !== undefined) {
    params.set('time', `${options.time}s`);
  }

  if (options?.width !== undefined) {
    params.set('width', options.width.toString());
  }

  if (options?.height !== undefined) {
    params.set('height', options.height.toString());
  }

  if (options?.fit) {
    params.set('fit', options.fit);
  }

  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return url;
}

/**
 * Get thumbnail URL (unified for both Cloudflare and Mux)
 */
export function getThumbnailUrl(video: VideoSource, options?: {
  time?: number;
  width?: number;
  height?: number;
}): string {
  // Priority 1: Cloudflare Stream thumbnail
  if (video.cf_stream_uid) {
    return getCloudflareThumbnailUrl(video.cf_stream_uid, options);
  }

  // Priority 2: Mux thumbnail
  if (video.mux_playback_id) {
    let url = `https://image.mux.com/${video.mux_playback_id}/thumbnail.jpg`;

    // Add optional query parameters for Mux
    const params = new URLSearchParams();

    if (options?.time !== undefined) {
      params.set('time', options.time.toString());
    }

    if (options?.width !== undefined) {
      params.set('width', options.width.toString());
    }

    if (options?.height !== undefined) {
      params.set('height', options.height.toString());
    }

    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    return url;
  }

  // Fallback: placeholder
  return '/placeholder.svg';
}

/**
 * Check if video has any playback source available
 */
export function hasPlaybackSource(video: VideoSource): boolean {
  return !!(video.cf_stream_uid || video.mux_playback_id);
}

/**
 * Get video display info (for UI decisions)
 */
export function getVideoDisplayInfo(video: VideoSource) {
  const cloudflareEnabled = isCloudflareStreamEnabled();
  const hasCloudflare = !!video.cf_stream_uid;
  const hasMux = !!video.mux_playback_id;
  const hasAnySource = hasCloudflare || hasMux;

  return {
    isReady: video.status === 'ready',
    isProcessing: video.status === 'processing',
    hasPlaybackSource: hasAnySource,
    hasCloudflareStream: hasCloudflare,
    hasMux: hasMux,
    canPlay: (video.status === 'ready' || video.status === 'processing') && hasAnySource,
    isLegacy: !!video.legacy_video_id,
    cloudflareEnabled,
    preferredSource: cloudflareEnabled && hasCloudflare ? 'cloudflare' : hasMux ? 'mux' : null,
  };
}

